import { Controller } from "@hotwired/stimulus"

// Выбор палет в заказ через select2 с серверным поиском (OrdersController#pallet_options).
// Сам select2 значение не хранит: после выбора палета уходит чипом в блок
// «Выбрано», а скрытое поле order[pallet_numbers] держит номера через запятую.
export default class extends Controller {
  static targets = ["field", "selected", "errors", "select"]
  static values = { optionsUrl: String }

  connect() {
    // номер → подпись («№29 · Люкс»)
    this.pallets = new Map()
    this.restoreFromField()
  }

  // После ошибки валидации (или при редактировании) в скрытом поле уже лежат
  // номера — подписи к ним запрашиваем у сервера одним запросом.
  async restoreFromField() {
    const numbers = this.numbersFromField()
    if (numbers.length === 0) return this.render()

    // Показываем сразу хотя бы номера, чтобы блок не мигал пустым.
    numbers.forEach((n) => this.pallets.set(n, `№${n}`))
    this.render()

    const labels = await this.fetchLabels(numbers)
    labels.forEach(({ id, text }) => this.pallets.set(Number(id), text))
    this.render()
  }

  numbersFromField() {
    if (!this.hasFieldTarget || !this.fieldTarget.value) return []
    return this.fieldTarget.value
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n))
  }

  async fetchLabels(numbers) {
    if (!this.hasOptionsUrlValue) return []

    const url = new URL(this.optionsUrlValue, window.location.origin)
    url.searchParams.set("numbers", numbers.join(","))
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) return []
      const data = await response.json()
      return data.results || []
    } catch {
      return []
    }
  }

  // Выбор в select2: забираем палету себе и сбрасываем селект под новый поиск.
  addFromSelect(event) {
    const select = event.target
    const number = parseInt(select.value, 10)
    if (Number.isNaN(number)) return

    this.clearErrors()
    const label = select.selectedOptions[0]?.textContent?.trim() || `№${number}`

    if (this.pallets.has(number)) {
      this.showErrors([`№${number} уже добавлена`])
    } else {
      this.pallets.set(number, label)
      this.render()
    }

    this.resetSelect(select)
  }

  resetSelect(select) {
    // Опции, накопленные ajax-адаптером, больше не нужны — оставляем пустую.
    select.innerHTML = '<option value=""></option>'
    const $ = window.jQuery
    // change.select2 обновляет только виджет и не всплывает обратно сюда.
    if ($ && $(select).data("select2")) $(select).val(null).trigger("change.select2")
  }

  remove(event) {
    event.preventDefault()
    const number = parseInt(event.currentTarget.dataset.number, 10)
    this.pallets.delete(number)
    this.render()
  }

  showErrors(messages) {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.classList.remove("d-none")
    this.errorsTarget.textContent = messages.join("; ")
  }

  clearErrors() {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.classList.add("d-none")
    this.errorsTarget.textContent = ""
  }

  render() {
    const sorted = [...this.pallets.keys()].sort((a, b) => a - b)
    if (this.hasFieldTarget) this.fieldTarget.value = sorted.join(",")
    // Уже выбранные палеты не должны попадать в выдачу поиска.
    if (this.hasSelectTarget) {
      this.selectTarget.dataset.select2ParamsValue = JSON.stringify({ exclude: sorted.join(",") })
    }
    if (!this.hasSelectedTarget) return

    this.selectedTarget.replaceChildren(
      ...sorted.map((number) => this.chip(number, this.pallets.get(number)))
    )
  }

  chip(number, label) {
    const badge = document.createElement("span")
    badge.className = "badge text-bg-primary"
    badge.textContent = label

    const close = document.createElement("button")
    close.type = "button"
    close.className = "btn-close btn-close-white ms-1"
    close.style.fontSize = ".6rem"
    close.dataset.action = "pallet-picker#remove"
    close.dataset.number = number
    close.setAttribute("aria-label", `Убрать палету №${number}`)

    badge.appendChild(close)
    return badge
  }
}
